/*
 *  Copyright 2019 Adobe Systems Incorporated. All rights reserved.
 *  This file is licensed to you under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License. You may obtain a copy
 *  of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software distributed under
 *  the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 *  OF ANY KIND, either express or implied. See the License for the specific language
 *  governing permissions and limitations under the License.
 *
 */
import React, { Component, ReactElement } from 'react';
import SearchHistory from '../../components/SearchDropdown/SearchDropdownHistory/SearchDropdownHistory';
import SearchDropDownQuickLinks from '../../components/SearchDropdown/SearchDropDownQuickLinks/SearchDropDownQuickLinks';
import SearchDropdownAutoComplete from '../../components/SearchDropdown/SearchDropdownAutoComplete/SearchDropdownAutoComplete';
import SearchDropdownApps from '../../components/SearchDropdown/SearchDropdownApps/SearchDropdownApps';
import AnalyticsUtils from '../../analytics/AnalyticsUtil';
import './SearchDropdown.scss';
import SearchDropDownRecommendations from '../../components/SearchDropdown/SearchDropDownRecommendations/SearchDropDownRecommendations';
import { ConnectedProps, connect } from 'react-redux';
import SearchDropdownPopularTemplates from '../../components/SearchDropdown/SearchDropdownPopularTemplates/SearchDropdownPopularTemplates';
import SearchDropDownQuickActions from '../../components/SearchDropdown/SearchDropDownQuickActions/SearchDropDownQuickActions';
import SearchDropdownFiles from '../../components/SearchDropdown/SearchDropdownFiles/SearchDropdownFiles';
import SearchDropdownOnboardingSummary from '../../components/SearchDropdown/SearchDropdownLeoOnboarding/SearchDropdownLeoOnboarding';
import SearchUtils from '../../utils/SearchUtils';
import SearchHostContext from '../../utils/SearchHostContext';
import { setSourceOfQuery } from '../../actions/searchActions';
import { getAppLocale } from '@intl/adobe-locales';

type Props = {
	searchHostContext: HostContext;
	logger: Logger;
	onSubmit: (...args: Array<any>) => any;
	searchQuery: string;
	showAutoComplete: boolean;
	hostShowSearchDropdown: boolean;
	searchConfig: Record<string, any>;
	getSearchBarContainerRef: (...args: Array<any>) => any;
} & PropsFromRedux;
/*
 * SearchDropdown for Search.
 */

class SearchDropdown extends Component<Props> {
	searchDropdownDivRef: React.RefObject<unknown>;
	/**
	 * Returns the render for the Search Dropdown
	 * @returns <element>
	 */

	constructor(props) {
		super(props);
		this.searchDropdownDivRef = React.createRef();
	}

	componentDidMount(): void {
		this.props.getSearchBarContainerRef(this.searchDropdownDivRef.current);
	}

	componentDidUpdate(prevProps: Readonly<Props>): void {
		if (prevProps.searchQuery !== this.props.searchQuery) {
			this.props.setSourceOfQuery('');
		}
	}

	getSearchDropdownRender() {
		const searchDropdown: ReactElement[] = [];
		const isOnboardingEnabledFromFloodgate = SearchUtils.isLeoFloodgateFeatureInTest(
			'ccs.ui.leo.onboarding',
			this.props.searchHostContext,
		);
		const searchQuicklinksBucket = AnalyticsUtils.experiment['20191111-search-quick-links'];
		// for CCD and other InAppClients
		const shouldShowQuickLinks =
			this.props.searchConfig &&
			this.props.searchConfig.ui &&
			this.props.searchConfig.ui.features &&
			this.props.searchConfig.ui.features.quickLinks;

		const showIntentActions =
			this.props.searchConfig &&
			this.props.searchConfig.ui &&
			this.props.searchConfig.ui.features &&
			this.props.searchConfig.ui.features.intents;

		const hostFilesPanelActive = SearchUtils.isHostFilesPanelActive();

		if (
			isOnboardingEnabledFromFloodgate &&
			this.props.onboardingSummaryItems &&
			this.props.searchQuery.length === 0 &&
			!hostFilesPanelActive
		) {
			searchDropdown.push(
				<SearchDropdownOnboardingSummary
					summaryData={this.props.onboardingSummaryItems}
					searchHostContext={this.props.searchHostContext}
					searchQuery={this.props.searchQuery}
					hostShowSearchDropdown={this.props.hostShowSearchDropdown}
					onSubmit={this.props.onSubmit}
					logger={this.props.logger}
				/>,
			);
		}

		if (this.props.searchQuery.length === 0) {
			if (SearchUtils.isSAYTFilesEnabled()) {
				searchDropdown.push(
					<SearchDropdownFiles
						logger={this.props.logger}
						hostShowSearchDropdown={this.props.hostShowSearchDropdown}
						onSubmit={this.props.onSubmit}
						variant='presearch'
					/>,
				);
			}
			searchDropdown.push(<SearchDropDownRecommendations />);
			if (!SearchUtils.isHostFilesPanelActive()) {
				searchDropdown.push(
					<SearchDropdownPopularTemplates
						searchHostContext={this.props.searchHostContext}
						searchQuery={this.props.searchQuery}
						logger={this.props.logger}
						key='search-dropdown-popular-templates'
						searchConfig={this.props.searchConfig}
					/>,
					<SearchDropDownQuickActions />,
				);
			}
		}

		if (SearchUtils.isSAYTFilesEnabled() && this.props.searchQuery.length) {
			searchDropdown.push(
				<SearchDropdownFiles
					logger={this.props.logger}
					hostShowSearchDropdown={this.props.hostShowSearchDropdown}
					onSubmit={this.props.onSubmit}
					variant='autocomplete'
				/>,
			);
		}

		// Show Search Quick Links if bucket is not control and query is two letters or more
		if (
			this.props.searchQuery.length > 1 &&
			((searchQuicklinksBucket !== 'control' && searchQuicklinksBucket !== undefined) ||
				shouldShowQuickLinks ||
				showIntentActions)
		) {
			searchDropdown.push(
				<SearchDropdownApps
					searchHostContext={this.props.searchHostContext}
					hostShowSearchDropdown={this.props.hostShowSearchDropdown}
					onSubmit={this.props.onSubmit}
				/>,
			);
		}

		// Show Search Recent Files

		// Show Search History and Autocomplete
		const { searchHistory, searchAutoComplete } = this.props.filteredSearchDropdownItems;
		if (searchHistory.length || searchAutoComplete.length) {
			searchDropdown.push(
				<div className='search-dropdown-history-autocomplete-container' role='group'>
					<SearchHistory
						searchHostContext={this.props.searchHostContext}
						searchQuery={this.props.searchQuery}
						hostShowSearchDropdown={this.props.hostShowSearchDropdown}
						onSubmit={this.props.onSubmit}
						logger={this.props.logger}
					/>
					<SearchDropdownAutoComplete
						searchHostContext={this.props.searchHostContext}
						searchQuery={this.props.searchQuery}
						hostShowSearchDropdown={this.props.hostShowSearchDropdown}
						onSubmit={this.props.onSubmit}
						logger={this.props.logger}
					/>
				</div>,
			);
		}

		searchDropdown.push(<SearchDropDownQuickLinks />);

		return searchDropdown;
	}

	render() {
		const locale = getAppLocale('ccsearch', SearchHostContext.getContext().locale, 'en');
		return (
			<div
				ref={this.searchDropdownDivRef as React.RefObject<HTMLDivElement>}
				className='search-dropdown-container'
				lang={locale}
			>
				<div
					className='search-dropdown-list'
					role='listbox'
					id='search-suggestions-list'
					aria-label='Search suggestions'
					aria-live='polite'
				>
					{this.getSearchDropdownRender()}
				</div>
			</div>
		);
	}
}

const mapStateToProps = ({ search }: RootState) => ({
	onboardingSummaryItems: search.leoOnboardingSummaryItems,
	filteredSearchDropdownItems: search.filteredSearchDropdownItems,
});

const mapDispatchToProps = {
	setSourceOfQuery,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(SearchDropdown);
